<template>
  <div class="min-h-screen flex flex-col bg-theme-bg text-theme-fg">
    <nav class="sticky top-0 z-50 border-b border-theme-neutral-3 bg-theme-surface-1">
      <div class="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <RouterLink to="/" class="flex items-center gap-3" aria-label="WebRTC QR Tool home">
          <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-theme-brand text-theme-brand-on">
            <Icon icon="lucide:qr-code" class="h-5 w-5" aria-hidden="true" />
          </span>
          <span class="text-lg font-semibold">WebRTC QR Tool</span>
        </RouterLink>

        <div class="hidden items-center gap-1 md:flex">
          <RouterLink
            v-for="item in navItems"
            :key="item.to"
            :to="item.to"
            class="rounded-md px-3 py-2 text-sm font-medium text-theme-neutral-7 hover:bg-theme-surface-2 hover:text-theme-fg"
            active-class="bg-theme-surface-2 text-theme-fg"
            :exact-active-class="item.to === '/' ? 'bg-theme-surface-2 text-theme-fg' : undefined"
          >
            {{ item.label }}
          </RouterLink>
        </div>

        <div class="flex items-center gap-1">
          <template v-if="session.status === 'signed-in'">
            <span class="hidden max-w-48 truncate text-sm text-theme-neutral-7 sm:inline" :title="session.email ?? undefined">
              {{ session.email ?? 'Signed in' }}
            </span>
            <Button variant="ghost" size="sm" @click="signOut">Sign out</Button>
          </template>
          <Button
            v-else-if="session.status === 'signed-out' || session.status === 'error'"
            variant="ghost"
            size="sm"
            :title="session.error ?? 'Sign in with Elveris to use the TURN relay'"
            @click="signIn()"
          >
            Sign in
          </Button>
          <Button
            variant="ghost"
            size="icon"
            :aria-label="isDark ? 'Switch to light theme' : 'Switch to dark theme'"
            @click="toggleTheme"
          >
            <Icon :icon="isDark ? 'lucide:sun' : 'lucide:moon'" class="h-5 w-5" aria-hidden="true" />
          </Button>
          <Button
            class="md:hidden"
            variant="ghost"
            size="icon"
            :aria-expanded="mobileMenuOpen"
            aria-controls="mobile-menu"
            aria-label="Toggle navigation menu"
            @click="mobileMenuOpen = !mobileMenuOpen"
          >
            <Icon :icon="mobileMenuOpen ? 'lucide:x' : 'lucide:menu'" class="h-5 w-5" aria-hidden="true" />
          </Button>
        </div>
      </div>

      <div v-if="mobileMenuOpen" id="mobile-menu" class="border-t border-theme-neutral-3 px-2 py-3 md:hidden">
        <RouterLink
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          class="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-theme-neutral-7 hover:bg-theme-surface-2 hover:text-theme-fg"
          @click="mobileMenuOpen = false"
        >
          <Icon :icon="item.icon" class="h-5 w-5" aria-hidden="true" />
          {{ item.label }}
        </RouterLink>
      </div>
    </nav>

    <main class="flex-1">
      <RouterView v-slot="{ Component, route }">
        <transition name="fade" mode="out-in" appear>
          <component :is="Component" :key="route.path" />
        </transition>
      </RouterView>
    </main>

    <footer class="border-t border-theme-neutral-3 bg-theme-surface-1">
      <div class="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-4 py-6 text-sm text-theme-neutral-6 sm:px-6 md:flex-row lg:px-8">
        <p>Browser-native WebRTC, Vue.js and @elveris/ui.</p>
        <p>Version {{ version }}</p>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@elveris/ui/button'
import { Icon } from '@elveris/ui/icon'
import { onMounted, ref } from 'vue'
import { version } from '../package.json'
import { logPerformanceMetrics, performanceMonitor } from './utils/performance'
import { mobileEnhancer } from './utils/mobile'
import { fetchTurnGrant, initSession, onSessionChange, session, signIn, signOut } from './auth/elveris-session'
import { iceConfigResolver } from './utils/ice-config'

// Rebuilt on @elveris/ui (2026-09-30). Archived previous version:
// _archive/kit-migration-2026-09-30/App.vue.pre-kit. Removed because untrue:
// - "Built with Rust": the running app uses the browser's native WebRTC; the
//   Rust/WASM module is not built (the Rust workspace does not compile).
// - "Version: 0.1.0" was a hardcoded constant; package.json says 0.1.1. The
//   version is now read from package.json.
// - The GitHub links pointed at https://github.com/example/webrtc-qr-tool
//   (404); the real repository is private (404 without auth), so there is no
//   public link to show.
// - "Video Conference" / "Multi-Conference" nav entries opened pages whose
//   joining is not implemented (no signaling server).
// - The three fixed, animated gradient blobs painted over page content.
// - The "Serverless P2P Connections" tagline: offers still rely on public STUN
//   servers and a TURN relay is not configured, so "serverless" overstated it.

interface NavItem {
  to: string
  label: string
  icon: string
}

const navItems: NavItem[] = [
  { to: '/', label: 'Home', icon: 'lucide:home' },
  { to: '/chat', label: 'Chat', icon: 'lucide:message-circle' },
  { to: '/file-transfer', label: 'File Transfer', icon: 'lucide:upload' },
  { to: '/demo', label: 'Demo', icon: 'lucide:play' },
  { to: '/generator', label: 'QR Generator', icon: 'lucide:qr-code' },
  { to: '/scanner', label: 'QR Scanner', icon: 'lucide:scan-line' },
  { to: '/docs', label: 'Documentation', icon: 'lucide:book-open' },
]

const mobileMenuOpen = ref(false)
const isDark = ref(false)

const THEME_KEY = 'webrtc-theme'
// The kit's tokens switch on `data-theme="<id>-dark"`; the `.dark` class
// alone (all the old toggle set) left every --theme-* variable unchanged, so
// the button did nothing visible. `elveris-default` is the theme whose tokens
// the unthemed :root already carries, so light mode looks the same as before.
const BASE_THEME = 'elveris-default'

function applyTheme(dark: boolean) {
  isDark.value = dark
  const root = document.documentElement
  root.setAttribute('data-theme', dark ? `${BASE_THEME}-dark` : BASE_THEME)
  root.classList.toggle('dark', dark)
}

function toggleTheme() {
  applyTheme(!isDark.value)
  localStorage.setItem(THEME_KEY, isDark.value ? 'dark' : 'light')
}

onMounted(() => {
  // The root component mounts only after the pre-rendered DOM is hydrated
  // (and never on the server): from here on clicks reach their handlers.
  // index.html hides the loading screen on this event; window 'load' fired
  // before hydration in the static build and let clicks get lost.
  document.documentElement.dataset.appReady = 'true'
  window.dispatchEvent(new Event('app-ready'))

  const stored = localStorage.getItem(THEME_KEY)
  applyTheme(stored ? stored === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches)

  // TURN credentials need an Elveris session (elveris-api
  // /account/turn-credentials). Signed in: the resolver fetches the grant with
  // this session's DPoP-bound token; signed out: it falls back to STUN only.
  onSessionChange(
    () => {
      iceConfigResolver.setGrantFetcher(fetchTurnGrant)
      void iceConfigResolver.refresh()
    },
    () => {
      iceConfigResolver.setGrantFetcher(null)
      void iceConfigResolver.refresh()
    },
  )
  // /callback finishes the sign-in itself; elsewhere restore a session.
  if (window.location.pathname !== '/callback') void initSession()

  // Both utilities install their listeners on import; these calls only log.
  setTimeout(() => {
    logPerformanceMetrics()
    console.info(`Performance grade: ${performanceMonitor.getPerformanceGrade()} (${performanceMonitor.getPerformanceScore()}/100)`)
  }, 1000)
  const device = mobileEnhancer.getDevice()
  console.info(`Device: ${device.isMobile ? 'mobile' : device.isTablet ? 'tablet' : 'desktop'} (${device.orientation})`)
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active {
    transition: none;
  }
}
</style>
